import React, { useState, useEffect, useCallback, useRef } from 'react';

// Extrai uma mensagem de erro exibível a partir de uma resposta de API.
// O `detail` pode vir como string, ou como lista de erros de validação do
// Pydantic (objetos { type, loc, msg, input, url }) — nesse caso, nunca
// renderizar o objeto direto como filho React.
function extractErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean).join('; ') || fallback;
  }
  if (typeof detail === 'object') return detail.msg || fallback;
  return fallback;
}

// ─── Deal Intelligence Tab (inline component) ─────────────────────────────────
function DealIntelligenceTab({ deal, api }) {
  const [insights, setInsights] = useState(null);
  const [scoring, setScoring] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (!deal?.id || loaded.current) return;
    loaded.current = true;
    setLoading(true);
    Promise.allSettled([
      api.get(`/deals/${deal.id}/ai-insights`),
      api.get(`/deals/${deal.id}/scoring`),
    ]).then(([insRes, scoreRes]) => {
      if (insRes.status === 'fulfilled') setInsights(insRes.value.data);
      if (scoreRes.status === 'fulfilled') setScoring(scoreRes.value.data);
      if (insRes.status === 'rejected' && scoreRes.status === 'rejected') setError('Não foi possível carregar a inteligência deste negócio.');
    }).finally(() => setLoading(false));
  }, [deal?.id]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-400">
      <div className="w-10 h-10 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin mb-4" />
      <p className="text-sm">Analisando negócio com IA…</p>
    </div>
  );
  if (error) return <p className="text-center py-12 text-red-400">{error}</p>;
  if (!insights && !scoring) return <p className="text-center py-12 text-slate-400">Sem dados de inteligência ainda.</p>;

  const riskPct = Math.round((insights?.risk_score || 0) * 100);
  const closePct = Math.round((insights?.close_probability || 0) * 100);
  const engPct = Math.round((scoring?.stage_score || 0));
  const overallScore = scoring?.overall_score || 0;

  return (
    <div className="space-y-5">
      {/* Score geral + risk */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-blue-700">{overallScore}</div>
          <div className="text-xs text-blue-600 mt-1">Score Geral</div>
          <div className="h-1.5 bg-blue-100 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${overallScore}%` }} />
          </div>
        </div>
        <div className={`border rounded-xl p-4 text-center ${closePct >= 70 ? 'bg-green-50 border-green-200' : closePct >= 40 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
          <div className={`text-3xl font-bold ${closePct >= 70 ? 'text-green-700' : closePct >= 40 ? 'text-amber-700' : 'text-red-700'}`}>{closePct}%</div>
          <div className={`text-xs mt-1 ${closePct >= 70 ? 'text-green-600' : closePct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>Prob. Fechamento</div>
        </div>
        <div className={`border rounded-xl p-4 text-center ${riskPct <= 30 ? 'bg-green-50 border-green-200' : riskPct <= 60 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
          <div className={`text-3xl font-bold ${riskPct <= 30 ? 'text-green-700' : riskPct <= 60 ? 'text-amber-700' : 'text-red-700'}`}>{riskPct}%</div>
          <div className={`text-xs mt-1 ${riskPct <= 30 ? 'text-green-600' : riskPct <= 60 ? 'text-amber-600' : 'text-red-600'}`}>Risco de Perda</div>
        </div>
      </div>

      {/* Score components */}
      {scoring && (
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3">Composição do Score</p>
          <div className="space-y-2">
            {[
              { label: 'Valor do negócio', val: scoring.value_score },
              { label: 'Estágio atual', val: scoring.stage_score },
              { label: 'Atividades recentes', val: scoring.activity_score },
              { label: 'Engajamento', val: scoring.engagement_score },
              { label: 'Prazo de fechamento', val: scoring.timeline_score },
            ].map(({ label, val }) => (
              <div key={label} className="flex items-center gap-3">
                <span className="text-xs text-slate-600 w-36 shrink-0">{label}</span>
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(100, val || 0)}%` }} />
                </div>
                <span className="text-xs font-semibold text-slate-700 w-8 text-right">{val || 0}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key insights */}
      {insights?.key_insights?.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2"><Ico e="💡" />Insights Principais</p>
          <div className="space-y-2">
            {insights.key_insights.map((ins, i) => (
              <div key={i} className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <span className="text-blue-500 mt-0.5 shrink-0">•</span>
                <p className="text-sm text-slate-700">{ins}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations / next best actions */}
      {(scoring?.next_best_actions?.length > 0 || insights?.recommendations?.length > 0) && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2"><Ico e="🎯" />Próximos Passos Recomendados</p>
          <div className="space-y-2">
            {(scoring?.next_best_actions || insights?.recommendations || []).map((action, i) => (
              <div key={i} className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg p-3">
                <div className="w-5 h-5 rounded-full bg-green-200 text-green-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                <p className="text-sm text-slate-700">{action}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Positive / Negative factors */}
      {scoring && (scoring.positive_factors?.length > 0 || scoring.negative_factors?.length > 0) && (
        <div className="grid grid-cols-2 gap-3">
          {scoring.positive_factors?.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-green-700 mb-2"><Ico e="✅" />Pontos Positivos</p>
              <ul className="space-y-1">
                {scoring.positive_factors.map((f, i) => <li key={i} className="text-xs text-green-800">· {f}</li>)}
              </ul>
            </div>
          )}
          {scoring.negative_factors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-red-700 mb-2"><Ico e="⚠" />Pontos de Atenção</p>
              <ul className="space-y-1">
                {scoring.negative_factors.map((f, i) => <li key={i} className="text-xs text-red-800">· {f}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Pagination } from './ui/pagination';
import { ListSkeleton, DashboardSkeleton } from './ui/skeleton';
import { usePagination } from '../hooks/usePagination';
import { useCache, useCachedAPI } from '../hooks/useCache';
import { useDebounce } from '../hooks/useDebounce';
import { useMobile } from '../hooks/useMobile';
import { compressJSON, decompressJSON } from '../utils/compression';
import { formatCurrency as formatCurrencyUtil, formatNumber, formatPercentage } from '../utils/formatters';
import DealAutomations from './deals/DealAutomations';
import Ico from './ui/Ico';

// ─── Helpers shared by tabs ───────────────────────────────────────────────────
const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(v || 0);

const QS_CFG = {
  draft:     { label: 'Rascunho',  cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  sent:      { label: 'Enviada',   cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  approved:  { label: 'Aprovada',  cls: 'bg-green-100 text-green-700 border-green-200' },
  rejected:  { label: 'Rejeitada', cls: 'bg-red-100 text-red-700 border-red-200' },
  converted: { label: 'Convertida',cls: 'bg-purple-100 text-purple-700 border-purple-200' },
};
const PS_CFG = {
  draft:    { label: 'Rascunho',  cls: 'bg-gray-100 text-gray-600' },
  sent:     { label: 'Enviada',   cls: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Aceita',    cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejeitada', cls: 'bg-red-100 text-red-700' },
  expired:  { label: 'Expirada',  cls: 'bg-amber-100 text-amber-700' },
};

// ─── Deal Quotes Tab ──────────────────────────────────────────────────────────
function DealQuotesTab({ deal, api }) {
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ product_name: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0 });
  const [form, setForm] = useState({
    title: '', description: '', payment_terms: '30 dias',
    delivery_terms: 'A combinar', warranty_terms: '12 meses',
    validity_days: 30, global_discount_percent: 0, shipping_cost: 0,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [qr, pr] = await Promise.allSettled([api.get('/quotes'), api.get('/products')]);
      const allQuotes = qr.status === 'fulfilled' ? (qr.value.data?.items || qr.value.data || []) : [];
      setQuotes(allQuotes.filter(q => q.deal_id === deal.id));
      setProducts(pr.status === 'fulfilled' ? (pr.value.data?.items || pr.value.data || []) : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [deal.id]);

  const openForm = () => {
    setForm({ title: `Cotação — ${deal.title || deal.name || ''}`, description: '', payment_terms: '30 dias', delivery_terms: 'A combinar', warranty_terms: '12 meses', validity_days: 30, global_discount_percent: 0, shipping_cost: 0 });
    setItems([]);
    setNewItem({ product_name: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0 });
    setShowForm(true);
  };

  const addItem = () => {
    if (!newItem.product_name || !newItem.unit_price) return toast.error('Preencha produto e preço');
    const sub = newItem.quantity * newItem.unit_price;
    const disc = sub * (newItem.discount_percent / 100);
    const taxable = sub - disc;
    const tax = taxable * (newItem.tax_percent / 100);
    setItems(prev => [...prev, { ...newItem, total: taxable + tax, id: Date.now().toString() }]);
    setNewItem({ product_name: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0 });
  };

  const save = async () => {
    if (!form.title) return toast.error('Título obrigatório');
    setSaving(true);
    try {
      await api.post('/quotes', {
        ...form,
        deal_id: deal.id,
        customer_name: deal.contact_name || '',
        customer_email: deal.contact_email || '',
        customer_phone: deal.contact_phone || '',
        customer_company: deal.organization_name || '',
        items,
      });
      toast.success('Cotação criada!');
      setShowForm(false);
      load();
    } catch { toast.error('Erro ao criar cotação'); }
    finally { setSaving(false); }
  };

  const sendQuote = async (id) => {
    try { await api.post(`/quotes/${id}/send`); toast.success('Cotação enviada!'); load(); }
    catch { toast.error('Erro ao enviar'); }
  };
  const approveQuote = async (id) => {
    try { await api.post(`/quotes/${id}/approve`, { action: 'approve', notes: '' }); toast.success('Cotação aprovada!'); load(); }
    catch { toast.error('Erro ao aprovar'); }
  };
  const convertQuote = async (id) => {
    try { await api.post(`/quotes/${id}/convert-to-proposal`); toast.success('Convertida em proposta!'); load(); }
    catch { toast.error('Erro ao converter'); }
  };

  const subtotal = items.reduce((s, i) => s + i.total, 0);
  const total = subtotal * (1 - form.global_discount_percent / 100) + Number(form.shipping_cost || 0);

  if (loading) return <div className="py-12 text-center text-gray-400">Carregando cotações…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{quotes.length} cotação(ões) neste negócio</p>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={openForm}>+ Nova Cotação</Button>
      </div>

      {quotes.length === 0 && !showForm && (
        <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
          <div className="text-4xl mb-2"><Ico e="💼" bare /></div>
          <p className="font-medium text-gray-600">Nenhuma cotação ainda</p>
          <p className="text-sm">Crie a primeira cotação para este negócio</p>
          <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white" onClick={openForm}>Criar Cotação</Button>
        </div>
      )}

      {/* Existing quotes */}
      {quotes.map(q => {
        const sc = QS_CFG[q.status] || QS_CFG.draft;
        return (
          <div key={q.id} className="border border-gray-200 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-gray-900">{q.title}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${sc.cls}`}>{sc.label}</span>
                </div>
                <p className="text-xs text-gray-400">Nº {q.quote_number} · Validade: {q.validity_days} dias</p>
                {q.items?.length > 0 && <p className="text-xs text-gray-500 mt-0.5">{q.items.length} item(ns) · {fmt(q.total_amount)}</p>}
              </div>
              <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                {q.status === 'draft' && (
                  <button onClick={() => sendQuote(q.id)} className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-600 hover:bg-blue-50"><Ico e="📧" />Enviar</button>
                )}
                {q.status === 'sent' && (
                  <button onClick={() => approveQuote(q.id)} className="text-xs px-2 py-1 rounded border border-green-200 text-green-600 hover:bg-green-50"><Ico e="✅" />Aprovar</button>
                )}
                {q.status === 'approved' && (
                  <button onClick={() => convertQuote(q.id)} className="text-xs px-2 py-1 rounded border border-purple-200 text-purple-600 hover:bg-purple-50"><Ico e="📄" />→ Proposta</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {/* Upload de Documentos */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="font-semibold text-gray-700 text-sm"><Ico e="📁" />Documentos da Cotação</p>
          <label className="cursor-pointer">
            <span className="text-xs px-3 py-1.5 rounded border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors">
              <Ico e="⬆" />Upload
            </span>
            <input
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png,.eml"
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                if (!files.length) return;
                for (const file of files) {
                  const formData = new FormData();
                  formData.append('file', file);
                  try {
                    await api.post(`/deals/${deal.id}/documents`, formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    toast.success(`"${file.name}" enviado!`);
                  } catch {
                    toast.error(`Erro ao enviar "${file.name}"`);
                  }
                }
                e.target.value = '';
              }}
            />
          </label>
        </div>
        <label className="block w-full cursor-pointer">
          <div className="border-2 border-dashed border-gray-200 rounded-xl py-8 text-center hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
            <div className="text-3xl mb-2"><Ico e="📂" bare /></div>
            <p className="text-sm text-gray-500">Arraste arquivos aqui ou clique para selecionar</p>
            <p className="text-xs text-gray-400 mt-1">PDF, Excel, Word, Imagens, Emails (.eml) — máx. 25 MB</p>
          </div>
          <input
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png,.eml"
            onChange={async (e) => {
              const files = Array.from(e.target.files);
              if (!files.length) return;
              for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                try {
                  await api.post(`/deals/${deal.id}/documents`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });
                  toast.success(`"${file.name}" enviado!`);
                } catch {
                  toast.error(`Erro ao enviar "${file.name}"`);
                }
              }
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {/* Inline creation form */}
      {showForm && (
        <div className="border-2 border-blue-200 rounded-xl p-5 bg-blue-50/30 space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-gray-800">Nova Cotação</p>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700">✕</button>
          </div>

          {/* Auto-populated client info banner */}
          <div className="bg-white border border-blue-100 rounded-lg px-3 py-2 text-xs text-gray-600 flex gap-4">
            <span><Ico e="👤" bare />{deal.contact_name || '—'}</span>
            <span><Ico e="📧" bare />{deal.contact_email || '—'}</span>
            <span><Ico e="🏢" bare />{deal.organization_name || '—'}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Título *</Label>
              <Input className="mt-1 text-sm" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">Prazo de Pagamento</Label>
              <Input className="mt-1 text-sm" value={form.payment_terms} onChange={e => setForm(f => ({...f, payment_terms: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">Validade (dias)</Label>
              <Input type="number" className="mt-1 text-sm" value={form.validity_days} onChange={e => setForm(f => ({...f, validity_days: Number(e.target.value)}))} />
            </div>
          </div>

          {/* Items */}
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-2">Itens</p>
            {items.map((it, i) => (
              <div key={it.id} className="flex items-center gap-2 text-xs bg-white border border-gray-100 rounded-lg px-3 py-1.5 mb-1">
                <span className="flex-1 font-medium">{it.product_name}</span>
                <span className="text-gray-500">{it.quantity}x {fmt(it.unit_price)}</span>
                <span className="font-semibold text-blue-700">{fmt(it.total)}</span>
                <button onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 ml-1">✕</button>
              </div>
            ))}
            <div className="grid grid-cols-5 gap-2 mt-2">
              <Input className="col-span-2 text-xs" placeholder="Produto" value={newItem.product_name} onChange={e => setNewItem(p => ({...p, product_name: e.target.value}))} />
              <Input type="number" className="text-xs" placeholder="Qtd" value={newItem.quantity} onChange={e => setNewItem(p => ({...p, quantity: Number(e.target.value)}))} />
              <Input type="number" className="text-xs" placeholder="Preço unit." value={newItem.unit_price} onChange={e => setNewItem(p => ({...p, unit_price: Number(e.target.value)}))} />
              <Button size="sm" variant="outline" onClick={addItem} className="text-xs">+ Item</Button>
            </div>
          </div>

          {/* Totals */}
          {items.length > 0 && (
            <div className="flex items-center justify-between text-sm border-t border-blue-100 pt-3">
              <div className="flex gap-4">
                <div>
                  <Label className="text-xs">Desconto global %</Label>
                  <Input type="number" className="w-20 text-xs mt-1" value={form.global_discount_percent} onChange={e => setForm(f => ({...f, global_discount_percent: Number(e.target.value)}))} />
                </div>
                <div>
                  <Label className="text-xs">Frete (R$)</Label>
                  <Input type="number" className="w-24 text-xs mt-1" value={form.shipping_cost} onChange={e => setForm(f => ({...f, shipping_cost: Number(e.target.value)}))} />
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total estimado</p>
                <p className="text-xl font-bold text-blue-700">{fmt(total)}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving}>{saving ? 'Salvando…' : 'Criar Cotação'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── File type helpers ────────────────────────────────────────────────────────
function fileIcon(mimetype, name) {
  if (!mimetype) mimetype = '';
  if (!name) name = '';
  const ext = name.split('.').pop()?.toLowerCase();
  if (mimetype.includes('pdf') || ext === 'pdf') return { icon: <Ico e="📄" bare />, color: 'text-red-600', bg: 'bg-red-50 border-red-200' };
  if (mimetype.includes('excel') || mimetype.includes('spreadsheet') || ext === 'xlsx' || ext === 'xls' || ext === 'csv')
    return { icon: <Ico e="📊" bare />, color: 'text-green-700', bg: 'bg-green-50 border-green-200' };
  if (mimetype.includes('word') || ext === 'doc' || ext === 'docx')
    return { icon: <Ico e="📝" bare />, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' };
  if (mimetype.includes('image') || ['png','jpg','jpeg','gif','webp'].includes(ext))
    return { icon: <Ico e="🖼" bare />, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' };
  if (mimetype.includes('rfc822') || ext === 'eml' || ext === 'msg')
    return { icon: <Ico e="📧" bare />, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' };
  return { icon: <Ico e="📎" bare />, color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200' };
}

function fmtFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Deal Documents Section ───────────────────────────────────────────────────
function DealDocumentsSection({ deal, api }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/deals/${deal.id}/documents`);
      setDocs(r.data || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [deal.id]);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let success = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/deals/${deal.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        success++;
      } catch (e) {
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }
    setUploading(false);
    if (success > 0) { toast.success(`${success} arquivo(s) enviado(s)`); load(); }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remover este documento?')) return;
    try {
      await api.delete(`/deals/documents/${docId}`);
      toast.success('Documento removido');
      load();
    } catch { toast.error('Erro ao remover'); }
  };

  const getDownloadUrl = (docId) => {
    const base = api.defaults?.baseURL || '';
    return `${base}/deals/documents/${docId}/download`;
  };

  const handlePreview = (doc) => {
    if (doc.mimetype?.includes('pdf')) setPreviewDoc(doc);
    else window.open(getDownloadUrl(doc.id), '_blank');
  };

  return (
    <div className="mt-8 border-t border-gray-100 pt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-lg"><Ico e="📁" bare /></span>
          <h3 className="font-semibold text-gray-800">Documentos do Cliente</h3>
          {docs.length > 0 && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{docs.length}</span>}
        </div>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="text-xs bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white px-3 py-1.5 rounded-lg font-medium transition flex items-center gap-1">
          {uploading ? <><span className="animate-spin"><Ico e="⏳" bare /></span> Enviando…</> : <><span><Ico e="⬆" bare /></span> Upload</>}
        </button>
        <input ref={fileInputRef} type="file" multiple className="hidden"
          accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.eml,.msg,.txt,.png,.jpg,.jpeg"
          onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !docs.length && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl transition-colors ${dragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-gray-50/50'} ${!docs.length ? 'cursor-pointer' : ''}`}
      >
        {loading ? (
          <div className="py-8 text-center text-gray-400 text-sm">Carregando…</div>
        ) : docs.length === 0 ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-2"><Ico e="📂" bare /></div>
            <p className="text-sm font-medium text-gray-600">Arraste arquivos aqui ou clique para selecionar</p>
            <p className="text-xs text-gray-400 mt-1">PDF, Excel, Word, Imagens, Emails (.eml) — máx. 25 MB</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {docs.map(doc => {
              const fi = fileIcon(doc.mimetype, doc.original_name);
              return (
                <div key={doc.id} className="flex items-center gap-3 px-4 py-3 hover:bg-white transition-colors group">
                  <div className={`w-9 h-9 rounded-lg border flex items-center justify-center text-lg flex-shrink-0 ${fi.bg}`}>
                    {fi.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.original_name}</p>
                    <p className="text-xs text-gray-400">
                      {fmtFileSize(doc.size)} · {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}
                      {doc.uploaded_by_name && ` · ${doc.uploaded_by_name}`}
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handlePreview(doc)} title="Visualizar / Abrir"
                      className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded transition"><Ico e="👁" bare /></button>
                    <a href={getDownloadUrl(doc.id)} download={doc.original_name} title="Baixar"
                      className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-2 py-1 rounded transition"><Ico e="⬇" bare /></a>
                    <button onClick={() => handleDelete(doc.id)} title="Remover"
                      className="text-xs bg-red-100 hover:bg-red-200 text-red-600 px-2 py-1 rounded transition"><Ico e="🗑" bare /></button>
                  </div>
                </div>
              );
            })}
            {/* Drop hint at bottom when has files */}
            <div className={`px-4 py-2 text-center text-xs text-gray-400 ${dragging ? 'bg-indigo-50 text-indigo-500' : ''}`}>
              {dragging ? '↓ Solte para adicionar' : 'Arraste mais arquivos aqui para adicionar'}
            </div>
          </div>
        )}
      </div>

      {/* PDF Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setPreviewDoc(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden" style={{height:'85vh'}} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <span><Ico e="📄" bare /></span>
                <span className="font-medium text-sm text-gray-800 truncate max-w-xs">{previewDoc.original_name}</span>
                <span className="text-xs text-gray-400">{fmtFileSize(previewDoc.size)}</span>
              </div>
              <div className="flex gap-2">
                <a href={getDownloadUrl(previewDoc.id)} download={previewDoc.original_name}
                  className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1.5 rounded-lg transition font-medium"><Ico e="⬇" />Baixar</a>
                <button onClick={() => setPreviewDoc(null)}
                  className="text-gray-400 hover:text-gray-700 text-xl font-bold px-2">✕</button>
              </div>
            </div>
            <iframe
              src={`${getDownloadUrl(previewDoc.id)}#toolbar=1&navpanes=1`}
              className="w-full" style={{height:'calc(100% - 56px)'}}
              title={previewDoc.original_name}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Deal Proposals Tab ───────────────────────────────────────────────────────
function DealProposalsTab({ deal, api }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ description: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0, total: 0 });
  const [form, setForm] = useState({ title: '', description: '', terms_conditions: '', validity_days: 30, client_name: '', client_email: '', client_company: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/proposals');
      const all = r.data?.items || r.data || [];
      setProposals(all.filter(p => p.deal_id === deal.id));
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [deal.id]);

  const openForm = (proposal) => {
    if (proposal) {
      setForm({
        title: proposal.title || '',
        description: proposal.description || '',
        terms_conditions: proposal.terms_conditions || '',
        validity_days: proposal.validity_days || 30,
        client_name: proposal.client_name || '',
        client_email: proposal.client_email || '',
        client_company: proposal.client_company || '',
      });
      setItems((proposal.items || []).map((it, i) => ({ ...it, id: it.id || `it-${i}` })));
      setEditingId(proposal.id);
    } else {
      setForm({ title: `Proposta — ${deal.title || deal.name || ''}`, description: '', terms_conditions: '', validity_days: 30, client_name: deal.contact_name || '', client_email: deal.contact_email || '', client_company: deal.organization_name || '' });
      setItems([]);
      setEditingId(null);
    }
    setNewItem({ description: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0, total: 0 });
    setShowForm(true);
  };

  const addItem = () => {
    if (!newItem.description) return toast.error('Descrição obrigatória');
    const sub = newItem.quantity * newItem.unit_price;
    const disc = sub * (newItem.discount_percent / 100);
    const taxable = sub - disc;
    const tax = taxable * (newItem.tax_percent / 100);
    setItems(prev => [...prev, { ...newItem, total: taxable + tax, id: Date.now().toString() }]);
    setNewItem({ description: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0, total: 0 });
  };

  const save = async () => {
    if (!form.title) return toast.error('Título obrigatório');
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        terms_conditions: form.terms_conditions,
        validity_days: form.validity_days,
        client_name: form.client_name || '',
        client_email: form.client_email || '',
        client_phone: deal.contact_phone || '',
        client_company: form.client_company || '',
        items,
      };
      if (editingId) {
        await api.put(`/proposals/${editingId}`, payload);
        toast.success('Proposta atualizada!');
      } else {
        await api.post('/proposals', { ...payload, deal_id: deal.id });
        toast.success('Proposta criada!');
      }
      setShowForm(false);
      setEditingId(null);
      load();
    } catch { toast.error(editingId ? 'Erro ao atualizar proposta' : 'Erro ao criar proposta'); }
    finally { setSaving(false); }
  };

  const totalValue = items.reduce((s, i) => s + i.total, 0);

  if (loading) return <div className="py-12 text-center text-gray-400">Carregando propostas…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{proposals.length} proposta(s) neste negócio</p>
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => openForm(null)}>+ Nova Proposta</Button>
      </div>

      {proposals.length === 0 && !showForm && (
        <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
          <div className="text-4xl mb-2"><Ico e="📄" bare /></div>
          <p className="font-medium text-gray-600">Nenhuma proposta ainda</p>
          <p className="text-sm">Crie ou converta uma cotação aprovada em proposta</p>
          <Button size="sm" className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => openForm(null)}>Criar Proposta</Button>
        </div>
      )}

      {proposals.map(p => {
        const sc = PS_CFG[p.status] || PS_CFG.draft;
        const total = (p.items || []).reduce((s, i) => s + (i.total || 0), 0);
        const hasEmail = (p.email_history?.length > 0) || !!p.last_email_body;
        return (
          <div key={p.id} className="border border-gray-200 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-gray-900">{p.title}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${sc.cls}`}>{sc.label}</span>
                  {(p.sent_count || 0) > 1 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Atualizada</span>
                  )}
                </div>
                <p className="text-xs text-gray-400">Nº {p.proposal_number} · Validade: {p.validity_days} dias</p>
                {total > 0 && <p className="text-xs text-indigo-600 font-medium mt-0.5">{fmt(total)}</p>}
                {p.sent_date && (
                  <p className="text-xs text-gray-400 mt-0.5"><Ico e="📧" />Último envio: {new Date(p.sent_date).toLocaleDateString('pt-BR')}</p>
                )}
              </div>
              <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                {['draft', 'sent', 'viewed'].includes(p.status) && (
                  <button onClick={() => setSendTarget(p)} className="text-xs px-2 py-1 rounded border border-indigo-200 text-indigo-600 hover:bg-indigo-50" title={p.status === 'draft' ? 'Enviar por e-mail' : 'Enviar atualização por e-mail'}><Ico e="📧" />Enviar</button>
                )}
                {hasEmail && (
                  <button onClick={() => setHistoryTarget(p)} className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50" title="Ver e-mails enviados"><Ico e="📤" />Ver e-mail</button>
                )}
                <button onClick={() => openForm(p)} className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50" title="Editar proposta"><Ico e="✏" />Editar</button>
              </div>
            </div>
          </div>
        );
      })}

      {showForm && (
        <div className="border-2 border-indigo-200 rounded-xl p-5 bg-indigo-50/30 space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-gray-800">{editingId ? 'Editar Proposta' : 'Nova Proposta'}</p>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-400 hover:text-gray-700">✕</button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Nome do cliente</Label>
              <Input className="mt-1 text-sm" placeholder="Nome completo" value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">E-mail</Label>
              <Input className="mt-1 text-sm" placeholder="email@exemplo.com" value={form.client_email} onChange={e => setForm(f => ({...f, client_email: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">Empresa</Label>
              <Input className="mt-1 text-sm" placeholder="Nome da empresa" value={form.client_company} onChange={e => setForm(f => ({...f, client_company: e.target.value}))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Título *</Label>
              <Input className="mt-1 text-sm" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Descrição</Label>
              <Textarea className="mt-1 text-sm" rows={2} placeholder="Descreva a proposta..." value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">Validade (dias)</Label>
              <Input type="number" className="mt-1 text-sm" value={form.validity_days} onChange={e => setForm(f => ({...f, validity_days: Number(e.target.value)}))} />
            </div>
            <div>
              <Label className="text-xs">Condições Gerais</Label>
              <Input className="mt-1 text-sm" placeholder="Ex: Pagamento em 30 dias" value={form.terms_conditions} onChange={e => setForm(f => ({...f, terms_conditions: e.target.value}))} />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-700 mb-2">Itens</p>
            {items.map((it, i) => (
              <div key={it.id} className="flex items-center gap-2 text-xs bg-white border border-gray-100 rounded-lg px-3 py-1.5 mb-1">
                <span className="flex-1">{it.description}</span>
                <span className="text-gray-500">{it.quantity}x {fmt(it.unit_price)}</span>
                <span className="font-semibold text-indigo-700">{fmt(it.total)}</span>
                <button onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} className="text-red-400 ml-1">✕</button>
              </div>
            ))}
            <div className="grid grid-cols-5 gap-2 mt-2">
              <Input className="col-span-2 text-xs" placeholder="Descrição" value={newItem.description} onChange={e => setNewItem(p => ({...p, description: e.target.value}))} />
              <Input type="number" className="text-xs" placeholder="Qtd" value={newItem.quantity} onChange={e => setNewItem(p => ({...p, quantity: Number(e.target.value)}))} />
              <Input type="number" className="text-xs" placeholder="Preço unit." value={newItem.unit_price} onChange={e => setNewItem(p => ({...p, unit_price: Number(e.target.value)}))} />
              <Button size="sm" variant="outline" onClick={addItem} className="text-xs">+ Item</Button>
            </div>
          </div>

          {items.length > 0 && (
            <div className="flex justify-end border-t border-indigo-100 pt-3">
              <div className="text-right">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-xl font-bold text-indigo-700">{fmt(totalValue)}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancelar</Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} disabled={saving}>{saving ? 'Salvando…' : (editingId ? 'Salvar Alterações' : 'Criar Proposta')}</Button>
          </div>
        </div>
      )}

      <ProposalSendModal proposal={sendTarget} open={!!sendTarget} onClose={() => setSendTarget(null)} api={api} onSent={load} />
      <ProposalEmailHistoryModal proposal={historyTarget} open={!!historyTarget} onClose={() => setHistoryTarget(null)} />

      <DealDocumentsSection deal={deal} api={api} />
    </div>
  );
}

// ─── Deal Projeto Tab ─────────────────────────────────────────────────────────
function DealProjetoTab({ deal, api, onNavigateToProject }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/projects').then(r => {
      const all = r.data?.projects || r.data || [];
      // Link by deal_id or by name similarity
      setProjects(all.filter(p => p.deal_id === deal.id || (deal.title && p.name?.toLowerCase().includes(deal.title?.toLowerCase()?.split(' ')[0]))));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [deal.id]);

  if (loading) return <div className="py-12 text-center text-gray-400">Carregando projetos…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{projects.length} projeto(s) vinculado(s)</p>
        <Button size="sm" variant="outline" onClick={() => onNavigateToProject && onNavigateToProject()}>
          <Ico e="🚀" />Ir para NexoProject
        </Button>
      </div>
      {projects.length === 0 ? (
        <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
          <div className="text-4xl mb-2"><Ico e="🚀" bare /></div>
          <p className="font-medium text-gray-600">Nenhum projeto vinculado</p>
          <p className="text-sm mt-1">Quando este negócio for ganho, crie o projeto de entrega no NexoProject</p>
          <Button size="sm" className="mt-3" variant="outline" onClick={() => onNavigateToProject && onNavigateToProject()}>
            Abrir NexoProject
          </Button>
        </div>
      ) : (
        projects.map(p => (
          <div key={p.id} className="border border-gray-200 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow cursor-pointer" onClick={() => onNavigateToProject && onNavigateToProject()}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-teal-400 flex items-center justify-center text-white font-bold text-sm">{(p.name || 'P')[0]}</div>
              <div>
                <p className="font-semibold text-gray-900">{p.name}</p>
                <p className="text-xs text-gray-400">{p.description || 'Sem descrição'}</p>
              </div>
              <span className="ml-auto text-xs text-blue-600 hover:underline">Ver projeto →</span>
            </div>
          </div>
        ))
      )}

      {/* Journey progress bar */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 mt-2">
        <p className="text-xs font-semibold text-gray-600 mb-3"><Ico e="📍" />Jornada do Lead</p>
        <div className="flex items-center gap-0">
          {[
            { label: 'Lead', done: true },
            { label: 'Negócio', done: true },
            { label: 'Cotação', done: false },
            { label: 'Proposta', done: false },
            { label: 'Projeto', done: projects.length > 0 },
          ].map((step, i, arr) => (
            <React.Fragment key={step.label}>
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${step.done ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-400'}`}>
                  {step.done ? '✓' : i + 1}
                </div>
                <span className={`text-xs mt-1 ${step.done ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>{step.label}</span>
              </div>
              {i < arr.length - 1 && <div className={`flex-1 h-0.5 mb-5 ${step.done && arr[i+1].done ? 'bg-blue-500' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Sent Proposals Modal ─────────────────────────────────────────────────────
function SentProposalsModal({ open, onClose, api }) {
  const [proposals, setProposals] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sendTarget, setSendTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.allSettled([
      api.get('/proposals?limit=100'),
      api.get('/deals?limit=100'),
    ]).then(([pRes, dRes]) => {
      const allProps = pRes.status === 'fulfilled' ? (pRes.value.data?.items || pRes.value.data || []) : [];
      const allDeals = dRes.status === 'fulfilled' ? (dRes.value.data?.items || dRes.value.data || []) : [];
      setProposals(allProps.filter(p => p.status === 'sent' || p.status === 'viewed'));
      setDeals(allDeals);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open, api, version]);

  const dealMap = {};
  deals.forEach(d => { dealMap[d.id] = d; });

  const sentStatus = {
    sent:   { label: 'Enviada',     cls: 'bg-blue-100 text-blue-700' },
    viewed: { label: 'Visualizada', cls: 'bg-yellow-100 text-yellow-800' },
  };

  const q = search.trim().toLowerCase();
  const filtered = proposals.filter(p => {
    if (!q) return true;
    const deal = p.deal_id ? dealMap[p.deal_id] : null;
    return [
      deal?.title || deal?.name,
      p.title,
      p.proposal_number,
      p.client_name,
      p.client_company,
    ].some(v => (v || '').toString().toLowerCase().includes(q));
  });

  const groups = [];
  const groupIndex = {};
  filtered.forEach(p => {
    const key = p.deal_id || '__none';
    let g = groupIndex[key];
    if (!g) {
      g = { dealId: p.deal_id, deal: p.deal_id ? dealMap[p.deal_id] : null, proposals: [] };
      groupIndex[key] = g;
      groups.push(g);
    }
    g.proposals.push(p);
  });

  const bySentDesc = (a, b) => ((b.sent_date || b.created_at || '') < (a.sent_date || a.created_at || '') ? -1 : 1);
  groups.forEach(g => g.proposals.sort(bySentDesc));
  groups.sort((a, b) => {
    const da = a.proposals[0]?.sent_date || a.proposals[0]?.created_at || '';
    const db = b.proposals[0]?.sent_date || b.proposals[0]?.created_at || '';
    return db < da ? -1 : 1;
  });

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setSearch(''); } }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span><Ico e="📧" bare /></span>
            <span>Propostas Enviadas</span>
          </DialogTitle>
          <DialogDescription>
            {proposals.length} proposta(s) enviada(s) por email, agrupadas por negócio
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label>Buscar</Label>
          <Input
            placeholder="Busque por negócio, proposta, número, cliente ou empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400">Carregando propostas…</div>
        ) : groups.length === 0 ? (
          <div className="text-center py-14 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
            <div className="text-4xl mb-2"><Ico e="📄" bare /></div>
            <p className="font-medium text-gray-600">Nenhuma proposta enviada</p>
            <p className="text-sm mt-1">
              {search ? 'Nenhum resultado para a busca.' : 'Envie uma proposta na aba Proposta de um negócio para vê-la aqui.'}
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((g, gi) => {
              const deal = g.deal;
              const groupTotal = g.proposals.reduce((s, p) => s + (p.total_amount || 0), 0);
              return (
                <div key={gi} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {deal ? deal.title : 'Sem negócio vinculado'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {g.proposals.length} proposta(s)
                        {deal && deal.value ? ` · Valor do negócio: ${fmt(deal.value)}` : ''}
                      </p>
                    </div>
                    {groupTotal > 0 && (
                      <span className="text-sm font-bold text-indigo-700">{fmt(groupTotal)}</span>
                    )}
                  </div>
                  <div className="divide-y divide-gray-100">
                    {g.proposals.map(p => {
                      const sc = sentStatus[p.status] || sentStatus.sent;
                      return (
                        <div key={p.id} className="px-4 py-3 bg-white">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold text-gray-900">{p.title}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${sc.cls}`}>{sc.label}</span>
                              </div>
                              <p className="text-xs text-gray-400">
                                Nº {p.proposal_number}
                                {p.client_name ? ` · ${p.client_name}` : ''}
                                {p.client_company ? ` · ${p.client_company}` : ''}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-sm font-bold text-green-600">{fmt(p.total_amount || 0)}</p>
                              <p className="text-xs text-gray-400">
                                Enviada em {p.sent_date ? new Date(p.sent_date).toLocaleDateString('pt-BR') : '-'}
                              </p>
                              {(p.sent_count || 0) > 1 && (
                                <p className="text-[10px] text-amber-700 font-medium">Atualizada ({p.sent_count}x)</p>
                              )}
                              {p.valid_until && (
                                <p className="text-xs text-gray-400">
                                  Válida até {new Date(p.valid_until).toLocaleDateString('pt-BR')}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 shrink-0">
                              {((p.email_history?.length > 0) || p.last_email_body) && (
                                <button
                                  onClick={() => setHistoryTarget(p)}
                                  className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                                >
                                  <Ico e="📤" />Ver e-mail
                                </button>
                              )}
                              <button
                                onClick={() => setSendTarget(p)}
                                className="text-xs px-2 py-1 rounded border border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                                title="Enviar atualização por e-mail"
                              >
                                <Ico e="📧" />Reenviar
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
      </Dialog>

      <ProposalSendModal proposal={sendTarget} open={!!sendTarget} onClose={() => setSendTarget(null)} api={api} onSent={() => setVersion(v => v + 1)} />
      <ProposalEmailHistoryModal proposal={historyTarget} open={!!historyTarget} onClose={() => setHistoryTarget(null)} />
    </>
  );
}

// ─── Proposal Send Modal (preview + confirm) ──────────────────────────────────
function ProposalSendModal({ proposal, open, onClose, api, onSent }) {
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !proposal?.id) return;
    setLoading(true);
    setPreview(null);
    api.get(`/proposals/${proposal.id}/email-preview`)
      .then(r => {
        setPreview(r.data || {});
        setSubject(r.data?.subject || '');
      })
      .catch(() => toast.error('Erro ao gerar prévia do e-mail'))
      .finally(() => setLoading(false));
  }, [open, proposal?.id, api]);

  const confirmSend = async () => {
    if (!proposal?.id) return;
    setSending(true);
    try {
      await api.post(`/proposals/${proposal.id}/send`, { subject });
      toast.success(preview?.is_update ? 'Proposta atualizada e reenviada!' : 'Proposta enviada com sucesso! 📧');
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Erro ao enviar proposta'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span><Ico e="📧" bare /></span>
            <span>Enviar Proposta</span>
          </DialogTitle>
          <DialogDescription>
            {proposal?.client_email ? `Para: ${proposal.client_email}` : 'Confira o e-mail antes de enviar'}
          </DialogDescription>
        </DialogHeader>

        {preview?.is_update && (
          <div className="bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500 rounded-lg px-4 py-3">
            <p className="text-sm font-semibold text-amber-800"><Ico e="✏" />Atualização de proposta</p>
            <p className="text-xs text-amber-700 mt-0.5">
              O e-mail deixará explícito que esta é uma atualização da proposta enviada anteriormente
              {preview.previous_sent_date ? ` em ${new Date(preview.previous_sent_date).toLocaleDateString('pt-BR')}` : ''}.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Assunto</Label>
          <Input value={subject} onChange={e => setSubject(e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <Label>Corpo do e-mail (prévia)</Label>
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            {loading ? (
              <div className="py-12 text-center text-gray-400">Gerando prévia…</div>
            ) : preview?.body_html ? (
              <iframe
                title="Prévia do e-mail"
                srcDoc={preview.body_html}
                className="w-full"
                style={{ height: '360px', border: 0 }}
                sandbox=""
              />
            ) : (
              <div className="py-12 text-center text-gray-400">Sem conteúdo disponível.</div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancelar</Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={confirmSend}
            disabled={loading || sending || !proposal?.client_email}
          >
            {sending ? 'Enviando…' : <><Ico e="📧" />Confirmar Envio</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Proposal Email History Modal (view sent emails) ──────────────────────────
function ProposalEmailHistoryModal({ proposal, open, onClose }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!open || !proposal) return null;

  const history = (proposal.email_history || []).map((e, i) => ({ ...e, _index: i }));

  const fallbackEntries = !history.length && (proposal.last_email_subject || proposal.last_email_body)
    ? [{ _index: 0, sent_at: proposal.sent_date, to_email: proposal.client_email, subject: proposal.last_email_subject, body_html: proposal.last_email_body, is_update: (proposal.sent_count || 0) > 1 }]
    : [];

  const entries = history.length ? history : fallbackEntries;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setExpandedId(null); } }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span><Ico e="📧" bare /></span>
            <span>E-mails enviados — {proposal.title || proposal.proposal_number}</span>
          </DialogTitle>
          <DialogDescription>
            {entries.length} envio(s) registrado(s) para {proposal.client_name || 'o cliente'}
          </DialogDescription>
        </DialogHeader>

        {entries.length === 0 ? (
          <div className="text-center py-12 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
            <div className="text-4xl mb-2"><Ico e="📭" bare /></div>
            <p className="font-medium text-gray-600">Nenhum e-mail enviado ainda</p>
            <p className="text-sm mt-1">Use o botão "Enviar" para mandar a proposta por e-mail.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map(entry => {
              const isOpen = expandedId === entry._index;
              return (
                <div key={entry._index} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : entry._index)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 text-sm truncate">{entry.subject}</p>
                        {entry.is_update && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 shrink-0">Atualização</span>
                        )}
                        {entry.delivery_status === 'failed' && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 shrink-0">Falha no envio</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        Para: {entry.to_email || '-'}
                        {entry.sent_at ? ` · ${new Date(entry.sent_at).toLocaleString('pt-BR')}` : ''}
                      </p>
                    </div>
                    <span className="text-xs text-indigo-600 shrink-0">{isOpen ? 'Ocultar ▲' : 'Ver e-mail ▼'}</span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-gray-100 bg-white">
                      {entry.body_html ? (
                        <iframe
                          title="E-mail enviado"
                          srcDoc={entry.body_html}
                          className="w-full"
                          style={{ height: '360px', border: 0 }}
                          sandbox=""
                        />
                      ) : (
                        <div className="py-10 text-center text-gray-400 text-sm">Conteúdo não disponível.</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Deals({ api, user, setCurrentPage }) {
  // Mobile detection  
  const { isMobile, isTablet } = useMobile();
  
  // Basic state
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSentProposals, setShowSentProposals] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealActivities, setDealActivities] = useState([]);
  const [dealProducts, setDealProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedDetailTab, setSelectedDetailTab] = useState('resumo');
  const [dealInsights, setDealInsights] = useState(null);
  const [dealScoring, setDealScoring] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [initializing, setInitializing] = useState(true);
  
  // Modal states
  const [showEditDealModal, setShowEditDealModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [showLostDialog, setShowLostDialog] = useState(false);
  const [lostReasonInput, setLostReasonInput] = useState('');
  const [lostCompetitor, setLostCompetitor] = useState('');
  
  // View controls
  const [viewMode, setViewMode] = useState('kanban'); // kanban, list, forecast
  const [sortBy, setSortBy] = useState('next_activity'); // next_activity, value, probability, name
  const [filterBy, setFilterBy] = useState('all'); // all, my_deals, won, lost
  
  // Search debouncing
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Cached API calls - SIMPLIFIED to avoid loops
  const fetchDealsWithCache = useCallback(async (params) => {
    // Aguarda o pipeline ser carregado antes de buscar, evitando uma
    // chamada prematura sem pipeline_id na montagem.
    if (!selectedPipeline?.id) {
      return { items: [], pagination: { total_items: 0, total_pages: 0, current_page: 1, per_page: 10 } };
    }
    try {
      // Build query params more safely
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || (isMobile ? 10 : 20)
      });
      
      // Only add optional params if they exist
      if (debouncedSearchTerm) {
        queryParams.append('search', debouncedSearchTerm);
      }
      if (selectedPipeline?.id) {
        queryParams.append('pipeline_id', selectedPipeline.id);
      }
      if (sortBy && sortBy !== 'next_activity') {
        queryParams.append('sort', sortBy);
      }
      if (filterBy && filterBy !== 'all') {
        queryParams.append('filter', filterBy);
      }
      
      const response = await api.get(`/deals?${queryParams}`);
      return response.data;
      
    } catch (error) {
      console.error('Error fetching deals:', error);
      return { items: [], pagination: { total_items: 0, total_pages: 0, current_page: 1, per_page: 10 } };
    }
  }, [api, debouncedSearchTerm, selectedPipeline?.id, sortBy, filterBy, isMobile]);
  
  // Pagination with caching
  const {
    data: deals,
    loading,
    error,
    pagination,
    goToPage,
    changePageSize,
    refresh,
    isEmpty,
    hasData
  } = usePagination(fetchDealsWithCache, { page: 1, limit: isMobile ? 10 : 20 });
  
  const [newProduct, setNewProduct] = useState({
    name: '',
    unit_price: 0,
    quantity: 1,
    discount_percent: 0,
    tax_percent: 0
  });

  const [newActivity, setNewActivity] = useState({
    activity_type: 'call',
    subject: '',
    description: '',
    due_date: '',
    duration_minutes: 30
  });

  const [newDeal, setNewDeal] = useState({
    title: '',
    value: 0,
    person_name: '',
    person_email: '',
    person_phone: '',
    organization_name: '',
    source: 'manual',
    expected_close_date: '',
    next_activity_type: '',
    next_activity_date: '',
    products: [],
    pipeline_id: ''
  });
  const [newDealValueText, setNewDealValueText] = useState('');
  const [dealFormErrors, setDealFormErrors] = useState({});
  const [pipelineLeads, setPipelineLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');

  useEffect(() => {
    // Simple initialization without complex calls
    const initialize = async () => {
      try {
        setInitializing(true);
        const converted = await fetchPipelines();
        if (converted.length > 0) {
          const def = converted[0];
          setSelectedPipeline(def);
          setNewDeal(prev => ({ ...prev, pipeline_id: def.id }));
        }
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        setInitializing(false);
      }
    };
    
    initialize();
  }, []); // Empty dependency array - only run once
  
  // O usePagination já re-busca automaticamente quando selectedPipeline?.id muda
  // (via a dependência no fetchDealsWithCache), portanto não é preciso um
  // useEffect adicional chamando refresh() aqui — isso causava chamada duplicada.

  const initializeDefaultPipeline = async () => {
    try {
      await api.post('/pipelines/initialize-default');
    } catch (error) {
      console.log('Default pipeline initialization:', error.response?.data?.message || 'Already exists');
    }
  };

  // Convert a funnel (stages as strings) into a pipeline-shaped object
  const funnelToPipeline = (funnel) => {
    const COLORS = ['#F59E0B','#3B82F6','#8B5CF6','#F97316','#10B981','#EF4444','#06B6D4','#84CC16'];
    const PROBS  = [10, 25, 40, 55, 70, 85, 90, 95];
    return {
      ...funnel,
      is_default: false,
      stages: (funnel.stages || []).map((name, i) => ({
        id: name,
        name,
        probability: PROBS[i] || 50,
        position: i,
        color: COLORS[i % COLORS.length]
      }))
    };
  };

  const fetchPipelines = async () => {
    try {
      // Use funnels as pipeline source so users see all their funnels
      const response = await api.get('/funnels/list');
      const raw = Array.isArray(response.data) ? response.data : [];
      const converted = raw.map(funnelToPipeline);
      setPipelines(converted);
      return converted;
    } catch (error) {
      console.error('Error fetching funnels as pipelines:', error);
      return [];
    }
  };



  // Re-fetch pipelines when Add modal opens (ensures fresh data)
  useEffect(() => {
    if (showAddModal && pipelines.length === 0) {
      fetchPipelines().then(data => {
        if (data && data.length > 0) {
          const def = data.find(p => p.is_default) || data[0];
          setNewDeal(prev => ({ ...prev, pipeline_id: def.id }));
        }
      });
    }
  }, [showAddModal]);

  // Fetch leads for selected pipeline/funnel in modal
  useEffect(() => {
    if (!showAddModal || !newDeal.pipeline_id) { setPipelineLeads([]); return; }
    const fetchLeadsForPipeline = async () => {
      try {
        // pipeline_id IS the funnel_id (we use funnels as pipelines)
        const params = new URLSearchParams({ limit: '100', funnel_id: newDeal.pipeline_id });
        const res = await api.get(`/leads?${params}`);
        const raw = res.data?.items || res.data || [];
        setPipelineLeads(Array.isArray(raw) ? raw : []);
      } catch (e) {
        setPipelineLeads([]);
      }
    };
    fetchLeadsForPipeline();
  }, [showAddModal, newDeal.pipeline_id]);

  const handleLeadSelect = (leadId) => {
    setSelectedLeadId(leadId);
    const lead = pipelineLeads.find(l => l.id === leadId);
    if (lead) {
      setNewDeal(prev => ({
        ...prev,
        person_name: lead.name || '',
        person_email: lead.email || '',
        person_phone: lead.phone || '',
        organization_name: lead.company || '',
        source: lead.source || prev.source
      }));
    }
  };

  const fetchDealActivities = async (dealId) => {
    try {
      const response = await api.get(`/deals/${dealId}/activities`);
      setDealActivities(response.data);
    } catch (error) {
      console.error('Erro ao carregar atividades:', error);
      setDealActivities([]);
    }
  };

  const handleViewDealDetails = (deal) => {
    setSelectedDeal(deal);
    setDealProducts(deal.products || []);
    setShowDetailModal(true);
    fetchDealActivities(deal.id);
  };

  const handleEditDeal = (deal) => {
    setEditingDeal({
      id: deal.id,
      title: deal.title,
      value: deal.value,
      stage: deal.stage,
      probability: deal.probability,
      person_name: deal.person_name,
      person_phone: deal.person_phone,
      person_email: deal.person_email,
      organization_name: deal.organization_name,
      expected_close_date: deal.expected_close_date ? new Date(deal.expected_close_date).toISOString().split('T')[0] : ''
    });
    setShowEditDealModal(true);
  };

  const handleSaveEditDeal = async () => {
    if (!editingDeal.title.trim()) {
      toast.error('Título do negócio é obrigatório');
      return;
    }

    try {
      const updateData = {
        title: editingDeal.title,
        value: parseFloat(editingDeal.value) || 0,
        stage: editingDeal.stage,
        probability: parseFloat(editingDeal.probability) || 0,
        person_name: editingDeal.person_name,
        person_phone: editingDeal.person_phone,
        person_email: editingDeal.person_email,
        organization_name: editingDeal.organization_name
      };

      if (editingDeal.expected_close_date) {
        updateData.expected_close_date = editingDeal.expected_close_date;
      }

      const res = await api.put(`/deals/${editingDeal.id}`, updateData);
      toast.success('Negócio atualizado com sucesso!');
      showAutomationResults(res.data?.automation_results);
      setShowEditDealModal(false);
      setEditingDeal(null);
      refresh();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar negócio'));
    }
  };

  const handleMarkDealAsWon = async (deal) => {
    try {
      const res = await api.put(`/deals/${deal.id}`, { status: 'won' });
      toast.success(`${deal.title}marcado como GANHO! `);
      showAutomationResults(res.data?.automation_results);
      refresh();
    } catch (error) {
      toast.error('Erro ao marcar negócio como ganho');
    }
  };

  const handleMarkDealAsLost = async (deal) => {
    try {
      const res = await api.put(`/deals/${deal.id}`, { status: 'lost' });
      toast.success(`${deal.title} marcado como perdido`);
      showAutomationResults(res.data?.automation_results);
      refresh();
    } catch (error) {
      toast.error('Erro ao marcar negócio como perdido');
    }
  };

  const handleAddProduct = () => {
    const product = {
      ...newProduct,
      id: Date.now().toString(),
      total: newProduct.unit_price * newProduct.quantity * (1 - newProduct.discount_percent / 100) * (1 + newProduct.tax_percent / 100)
    };
    
    setDealProducts([...dealProducts, product]);
    setNewProduct({
      name: '',
      unit_price: 0,
      quantity: 1,
      discount_percent: 0,
      tax_percent: 0
    });
    setShowProductModal(false);
  };

  const handleAddActivity = async () => {
    if (!selectedDeal) return;
    
    try {
      await api.post(`/deals/${selectedDeal.id}/activities`, newActivity);
      toast.success('Atividade adicionada com sucesso!');
      setNewActivity({
        activity_type: 'call',
        subject: '',
        description: '',
        due_date: '',
        duration_minutes: 30
      });
      fetchDealActivities(selectedDeal.id);
    } catch (error) {
      toast.error('Erro ao adicionar atividade');
    }
  };

  const handleMarkAsWon = async () => {
    if (!selectedDeal) return;
    
    try {
      await api.put(`/deals/${selectedDeal.id}`, {
        status: 'won',
        won_reason: 'Fechamento bem-sucedido'
      });
      toast.success("Negócio marcado como ganho! ");
      setShowDetailModal(false);
      refresh();
    } catch (error) {
      toast.error('Erro ao marcar como ganho');
    }
  };

  const handleMarkAsLost = () => {
    setLostReasonInput('');
    setLostCompetitor('');
    setShowLostDialog(true);
  };

  const handleConfirmLost = async () => {
    if (!selectedDeal) return;
    try {
      await api.put(`/deals/${selectedDeal.id}`, {
        status: 'lost',
        lost_reason: lostReasonInput || 'Não especificado',
        lost_to_competitor: lostCompetitor || null
      });
      toast.success('Negócio marcado como perdido');
      setShowLostDialog(false);
      setShowDetailModal(false);
      refresh();
    } catch (error) {
      toast.error('Erro ao marcar como perdido');
    }
  };

const handleDeleteDeal = async (deal) => {
    if (!deal) return;
    if (!window.confirm(`Tem certeza que deseja excluir o negócio "${deal.title}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      await api.delete(`/deals/${deal.id}`);
      toast.success('Negócio excluído com sucesso');
      if (showDetailModal) setShowDetailModal(false);
      refresh();
    } catch (error) {
      toast.error('Erro ao excluir negócio');
    }
  };  

  const validateNewDeal = () => {
    const errors = {};
    const chosenPipelineId = newDeal.pipeline_id || selectedPipeline?.id;

    if (!chosenPipelineId) errors.pipeline_id = 'Selecione um pipeline';
    if (!newDeal.title.trim()) errors.title = 'Informe o título do negócio';
    if (!newDeal.person_name.trim()) errors.person_name = 'Informe o nome do contato';
    if (newDeal.person_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newDeal.person_email)) {
      errors.person_email = 'E-mail inválido';
    }

    setDealFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddDeal = async () => {
  if (!validateNewDeal()) {
    toast.error('Preencha os campos obrigatórios destacados');
    return;
  }

  try {
    const chosenPipelineId = newDeal.pipeline_id || selectedPipeline?.id;

    const parsedValue = parseFloat(
      newDealValueText.replace(/\./g, '').replace(',', '.') || '0'
    ) || 0;

    const dealData = {
      title: newDeal.title.trim(),
      value: parsedValue,
      pipeline_id: chosenPipelineId,
      person_name: newDeal.person_name.trim(),
      person_email: newDeal.person_email || '',
      person_phone: newDeal.person_phone || null,
      organization_name: newDeal.organization_name || null,
      source: newDeal.source || 'manual',
      expected_close_date: newDeal.expected_close_date || null,
      next_activity_type: newDeal.next_activity_type || null,
      next_activity_date: newDeal.next_activity_date || null,
      products: [],
    };

    await api.post('/deals', dealData);
    toast.success('Negócio criado com sucesso!');
    setShowAddModal(false);
    resetNewDeal();
    refresh();
  } catch (error) {
    toast.error(extractErrorMessage(error, 'Erro ao criar negócio'));
  }
};

const resetNewDeal = () => {
  setNewDealValueText('');
  setSelectedLeadId('');
  setPipelineLeads([]);
  setDealFormErrors({});
  setNewDeal({
    title: '',
    value: 0,
    person_name: '',
    person_email: '',
    person_phone: '',
    organization_name: '',
    source: 'manual',
    expected_close_date: '',
    next_activity_type: '',
    next_activity_date: '',
    products: [],
    pipeline_id: selectedPipeline?.id || ''
  });
};

  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const showAutomationResults = (results) => {
    if (!results || !Array.isArray(results)) return;
    results.forEach((r) => {
      const msg = r.automation ? `⚙️ ${r.automation}: ${r.message}` : `⚙️ ${r.message}`;
      if (r.success) {
        toast.success(msg);
      } else {
        toast.error(msg);
      }
    });
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    
    if (!draggedDeal || draggedDeal.stage === targetStage) {
      setDraggedDeal(null);
      return;
    }

    try {
      const res = await api.put(`/deals/${draggedDeal.id}`, {
        stage: targetStage
      });

      // Refresh the deals data to reflect the change
      refresh();

      toast.success(`Negócio movido para ${targetStage}`);
      showAutomationResults(res.data?.automation_results);
    } catch (error) {
      toast.error('Erro ao mover negócio');
    } finally {
      setDraggedDeal(null);
    }
  };

  const getDealsByStage = (stageName) => {
    return deals.filter(deal => deal.stage === stageName);
  };

  const getStageStats = (stageName) => {
    const stageDeals = getDealsByStage(stageName);
    const totalValue = stageDeals.reduce((sum, deal) => sum + deal.value, 0);
    return {
      count: stageDeals.length,
      value: totalValue,
      percentage: deals.length > 0 ? Math.round((stageDeals.length / deals.length) * 100) : 0
    };
  };

  const formatCurrency = (value) => {
    return formatCurrencyUtil(value);
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'call': return '📞';
      case 'email': return '📧';
      case 'meeting': return '🤝';
      case 'task': return '✅';
      default: return '📋';
    }
  };

  const getActivityColor = (date) => {
    if (!date) return 'text-slate-400';
    const activityDate = new Date(date);
    const today = new Date();
    const diffDays = Math.ceil((activityDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'text-red-600'; // Overdue
    if (diffDays === 0) return 'text-orange-600'; // Today
    if (diffDays <= 3) return 'text-yellow-600'; // Soon
    return 'text-green-600'; // Future
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 75) return 'bg-green-100 text-green-800';
    if (probability >= 50) return 'bg-blue-100 text-blue-800';
    if (probability >= 25) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (loading || initializing) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-8 bg-slate-200 rounded w-48 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-64"></div>
            </div>
            <div className="h-10 bg-slate-200 rounded w-32"></div>
          </div>
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6">
              <div className="h-4 bg-slate-200 rounded w-1/4 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deals-page">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Negócios</h1>
          <p className="text-slate-600 mt-1">Gerencie seu pipeline de vendas</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {/* View Mode Toggle */}
          <div className="flex flex-wrap items-center gap-1 bg-slate-100 p-1 rounded-lg">
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
            >
              <Ico e="📊" />Kanban
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <Ico e="📋" />Lista
            </Button>
            <Button
              variant={viewMode === 'forecast' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('forecast')}
            >
              <Ico e="📈" />Previsão
            </Button>
            <Button
              variant={viewMode === 'automations' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('automations')}
              className={viewMode === 'automations' ? '' : 'text-purple-600 hover:bg-purple-50'}
            >
              <Ico e="⚙" />Automações
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={() => setShowSentProposals(true)}
            className="border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            <Ico e="📧" />Propostas Enviadas
          </Button>

          <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) setDealFormErrors({}); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary">
                <Ico e="➕" />Novo Negócio
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </div>

      {/* Pipeline Selector & Controls */}
      <Card className="glass border-white/20">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Pipeline Selector */}
            <div className="flex flex-wrap items-center gap-2">
              <Label>Pipeline:</Label>
              <Select
                value={selectedPipeline?.id || ''}
                onValueChange={(value) => {
                  const pipeline = pipelines.find(p => p.id === value);
                  setSelectedPipeline(pipeline);
                }}
              >
                <SelectTrigger className="w-48 sm:w-64 bg-white/50">
                  <SelectValue placeholder="Selecione um pipeline" />
                </SelectTrigger>
                <SelectContent>
                  {pipelines.map((pipeline) => (
                    <SelectItem key={pipeline.id} value={pipeline.id}>
                      {pipeline.name} {pipeline.is_default ? '(Padrão)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort and Filter Controls */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <Label>Ordenar:</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40 sm:w-48 bg-white/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="next_activity"><Ico e="📅" />Próxima Atividade</SelectItem>
                    <SelectItem value="value"><Ico e="💰" />Valor</SelectItem>
                    <SelectItem value="probability"><Ico e="📊" />Probabilidade</SelectItem>
                    <SelectItem value="name"><Ico e="🔤" />Nome</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Label>Filtrar:</Label>
                <Select value={filterBy} onValueChange={setFilterBy}>
                  <SelectTrigger className="w-36 sm:w-40 bg-white/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all"><Ico e="🎯" />Todos</SelectItem>
                    <SelectItem value="my_deals"><Ico e="👤" />Meus</SelectItem>
                    <SelectItem value="won"><Ico e="✅" />Ganhos</SelectItem>
                    <SelectItem value="lost"><Ico e="❌" />Perdidos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Statistics */}
      {selectedPipeline && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {selectedPipeline.stages.map((stage) => {
            const stats = getStageStats(stage.name);
            return (
              <Card key={stage.id} className="glass border-white/20">
                <CardContent className="p-4 text-center">
                  <div 
                    className="w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: stage.color }}
                  >
                    {stats.count}
                  </div>
                  <p className="text-xs font-medium text-slate-700">{stage.name}</p>
                  <p className="text-xs text-slate-500">{formatCurrency(stats.value)}</p>
                  <p className="text-xs text-slate-400">{stats.percentage}%</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && selectedPipeline && (
        <div className="bg-white rounded-lg border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Negócio</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Contato</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Valor</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Estágio</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Probabilidade</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Próxima Atividade</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Ações</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal) => (
                  <tr key={deal.id} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{deal.title}</p>
                        <p className="text-sm text-slate-600">{deal.organization_name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-slate-900">{deal.person_name}</p>
                        <p className="text-xs text-slate-500">{deal.person_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-green-600">{formatCurrencyUtil(deal.value)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${getProbabilityColor(deal.probability)} text-white`}>
                        {deal.stage}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-slate-200 rounded-full h-2">
                          <div 
                            className="h-2 bg-blue-500 rounded-full" 
                            style={{ width: `${deal.probability || 0}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-slate-600">{deal.probability || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {deal.next_activity_date ? (
                        <div className={`text-xs ${getActivityColor(deal.next_activity_date)}`}>
                          <p>{getActivityIcon(deal.next_activity_type)} {deal.next_activity_type}</p>
                          <p>{new Date(deal.next_activity_date).toLocaleDateString('pt-BR')}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Nenhuma</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedDeal(deal);
                            setShowDetailModal(true);
                          }}
                          title="Ver detalhes"
                        >
                          <Ico e="👁" bare />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditDeal(deal)}
                          title="Editar"
                        >
                          <Ico e="✏" bare />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMarkDealAsWon(deal)}
                          className="text-green-600 hover:bg-green-100"
                          title="Marcar como ganho"
                        >
                          <Ico e="✅" bare />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMarkDealAsLost(deal)}
                          className="text-red-600 hover:bg-red-100"
                          title="Marcar como perdido"
                        >
                          <Ico e="❌" bare />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {deals.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <div className="w-16 h-16 mx-auto mb-4 opacity-50"><Ico e="💼" bare /></div>
                <p>Nenhum negócio encontrado</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Forecast View */}
      {viewMode === 'forecast' && selectedPipeline && (
        <div className="space-y-6">
          {/* Forecast Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="glass border-white/20">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl"><Ico e="💰" bare /></span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Valor Total Pipeline</p>
                    <p className="text-xl font-bold text-slate-900">
                      {formatCurrencyUtil(deals.reduce((sum, deal) => sum + (deal.value || 0), 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass border-white/20">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl"><Ico e="📊" bare /></span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Valor Ponderado</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrencyUtil(deals.reduce((sum, deal) => sum + ((deal.value || 0) * (deal.probability || 0) / 100), 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass border-white/20">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl"><Ico e="🎯" bare /></span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Taxa Conversão Média</p>
                    <p className="text-xl font-bold text-purple-600">
                      {formatPercentage(deals.length > 0 ? deals.reduce((sum, deal) => sum + (deal.probability || 0), 0) / deals.length : 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass border-white/20">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl"><Ico e="📈" bare /></span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Ticket Médio</p>
                    <p className="text-xl font-bold text-orange-600">
                      {formatCurrencyUtil(deals.length > 0 ? deals.reduce((sum, deal) => sum + (deal.value || 0), 0) / deals.length : 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Forecast by Stage */}
          <Card className="glass border-white/20">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span className="text-xl"><Ico e="📊" bare /></span>
                <span>Previsão por Estágio</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {selectedPipeline.stages.map((stage) => {
                  const stageDeals = getDealsByStage(stage.name);
                  const stageValue = stageDeals.reduce((sum, deal) => sum + (deal.value || 0), 0);
                  const weightedValue = stageDeals.reduce((sum, deal) => sum + ((deal.value || 0) * (deal.probability || 0) / 100), 0);
                  
                  return (
                    <div key={stage.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: stage.color }}
                        ></div>
                        <div>
                          <p className="font-medium text-slate-900">{stage.name}</p>
                          <p className="text-sm text-slate-600">{stageDeals.length} negócios</p>
                        </div>
                      </div>
                      
                      <div className="text-right space-y-1">
                        <div className="flex items-center space-x-4">
                          <div>
                            <p className="text-sm text-slate-600">Valor Total</p>
                            <p className="font-semibold text-slate-900">{formatCurrencyUtil(stageValue)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-600">Valor Ponderado</p>
                            <p className="font-semibold text-green-600">{formatCurrencyUtil(weightedValue)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-600">Probabilidade Média</p>
                            <p className="font-semibold text-blue-600">
                              {formatPercentage(stageDeals.length > 0 ? stageDeals.reduce((sum, deal) => sum + (deal.probability || 0), 0) / stageDeals.length : 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Top Deals */}
          <Card className="glass border-white/20">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span className="text-xl"><Ico e="🏆" bare /></span>
                <span>Maiores Oportunidades</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {deals
                  .sort((a, b) => (b.value || 0) - (a.value || 0))
                  .slice(0, 5)
                  .map((deal, index) => (
                    <div key={deal.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-bold text-blue-600">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{deal.title}</p>
                          <p className="text-sm text-slate-600">{deal.person_name} • {deal.organization_name}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-bold text-green-600">{formatCurrencyUtil(deal.value)}</p>
                        <p className="text-sm text-slate-600">{deal.probability || 0}% • {deal.stage}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Deal Automations View */}
      {viewMode === 'automations' && selectedPipeline && (
        <DealAutomations api={api} pipeline={selectedPipeline} />
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && selectedPipeline && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {selectedPipeline.stages.map((stage) => (
            <div
              key={stage.id}
              className="bg-slate-50 rounded-xl p-4 min-h-[500px]"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.name)}
            >
              {/* Stage Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: stage.color }}
                  />
                  <h3 className="font-semibold text-slate-800">{stage.name}</h3>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {getDealsByStage(stage.name).length}
                </Badge>
              </div>

              {/* Stage Content */}
              <div className="space-y-3">
                {getDealsByStage(stage.name).map((deal) => (
                  <div
                    key={deal.id}
                    className="bg-white rounded-lg p-4 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group relative"
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal)}
                    onClick={() => handleViewDealDetails(deal)}
                  >
                    {/* Deal Header */}
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-slate-900 text-sm leading-tight flex-1">
                        {deal.title}
                      </h4>
                      <Badge className={`text-xs ml-2 ${getProbabilityColor(deal.probability)}`}>
                        {deal.probability}%
                      </Badge>
                    </div>
                    
                    {/* Deal Value */}
                    <div className="text-lg font-bold text-green-600 mb-2 break-words">
                      {formatCurrency(deal.value)}
                    </div>
                    
                    {/* Contact Info */}
                    <div className="space-y-1 text-xs text-slate-600 mb-3">
                      <p className="truncate"><Ico e="👤" bare />{deal.person_name}</p>
                      {deal.organization_name && (
                        <p className="truncate"><Ico e="🏢" bare />{deal.organization_name}</p>
                      )}
                    </div>
                    
                    {/* Next Activity */}
                    {deal.next_activity_date && (
                      <div className={`text-xs ${getActivityColor(deal.next_activity_date)} mb-2 flex items-center space-x-1`}>
                        <span>{getActivityIcon(deal.next_activity_type)}</span>
                        <span>
                          {new Date(deal.next_activity_date).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    )}
                    
                    {/* Deal Footer */}
                    <div className="flex items-center justify-between text-xs text-slate-400 pt-2 border-t border-slate-100 group-hover:border-slate-200 transition-colors">
                      <span>
                        {new Date(deal.created_at).toLocaleDateString('pt-BR')}
                      </span>
                      <div className="flex items-center space-x-2">
                        {user?.role === 'admin' && deal.owner_name && (
                          <span className="text-blue-600"><Ico e="👤" bare />{deal.owner_name}</span>
                        )}
                        <span className="capitalize">{deal.source}</span>
                      </div>
                    </div>

                    {/* Action Buttons - Show on Hover */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 bg-white/80 hover:bg-blue-100 text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditDeal(deal);
                        }}
                        title="Editar negócio"
                      >
                        <Ico e="✏" bare />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 bg-white/80 hover:bg-green-100 text-green-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkDealAsWon(deal);
                        }}
                        title="Marcar como ganho"
                      >
                        <Ico e="✅" bare />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 bg-white/80 hover:bg-red-100 text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkDealAsLost(deal);
                        }}
                        title="Marcar como perdido"
                      >
                        <Ico e="❌" bare />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 bg-white/80 hover:bg-red-100 text-red-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDeal(deal);
                        }}
                        title="Excluir negócio"
                      >
                        <Ico e="🗑" bare />
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Empty State */}
                {getDealsByStage(stage.name).length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <div className="w-8 h-8 mx-auto mb-2 opacity-50"><Ico e="💼" bare /></div>
                    <p className="text-xs">Nenhum negócio</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Deal Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) setDealFormErrors({}); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Negócio</DialogTitle>
            <DialogDescription>Cadastre um novo negócio no pipeline</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">

            {/* STEP 1: Pipeline */}
            <div className="space-y-2">
              <Label>1. Pipeline *</Label>
              <Select
                value={newDeal.pipeline_id}
                onValueChange={(value) => {
                  setNewDeal({...newDeal, pipeline_id: value});
                  setSelectedLeadId('');
                  setDealFormErrors(prev => ({ ...prev, pipeline_id: undefined }));
                }}
              >
                <SelectTrigger className={dealFormErrors.pipeline_id ? 'border-red-400 focus:ring-red-300' : ''}>
                  <SelectValue placeholder="Selecione um pipeline" />
                </SelectTrigger>
                <SelectContent>
                  {pipelines.length === 0 && (
                    <SelectItem value="__loading__" disabled>Carregando pipelines...</SelectItem>
                  )}
                  {pipelines.map((pipeline) => (
                    <SelectItem key={pipeline.id} value={pipeline.id}>
                      {pipeline.name} • {pipeline.stages?.length || 0} estágios
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dealFormErrors.pipeline_id && (
                <p className="text-xs text-red-600"><Ico e="⚠" bare />{dealFormErrors.pipeline_id}</p>
              )}
            </div>

            {/* STEP 2: Lead selector (auto-populates contact fields) */}
            {newDeal.pipeline_id && (
              <div className="space-y-2">
                <Label>2. Lead do Pipeline</Label>
                <Select
                  value={selectedLeadId}
                  onValueChange={handleLeadSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={pipelineLeads.length === 0 ? 'Nenhum lead neste pipeline' : 'Selecionar lead (auto-preenche os dados)'} />
                  </SelectTrigger>
                  <SelectContent>
                    {pipelineLeads.map((lead) => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.name} {lead.company ? `• ${lead.company}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedLeadId && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <Ico e="✅" />Dados do lead preenchidos automaticamente — edite se necessário
                  </p>
                )}
              </div>
            )}

            <div className="border-t pt-3 space-y-4">
              {/* Title + Value */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Título do Negócio *</Label>
                  <Input
                    value={newDeal.title}
                    onChange={(e) => {
                      setNewDeal({...newDeal, title: e.target.value});
                      setDealFormErrors(prev => ({ ...prev, title: undefined }));
                    }}
                    placeholder="Ex: Venda sistema ERP"
                    className={dealFormErrors.title ? 'border-red-400 focus-visible:ring-red-300' : ''}
                  />
                  {dealFormErrors.title && <p className="text-xs text-red-600"><Ico e="⚠" bare />{dealFormErrors.title}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Valor (R$)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={newDealValueText}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      if (!digits) { setNewDealValueText(''); return; }
                      const cents = parseInt(digits, 10);
                      const formatted = (cents / 100).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      });
                      setNewDealValueText(formatted);
                    }}
                    placeholder="0,00"
                  />
                </div>
              </div>

              {/* Contact fields */}
              <div className="space-y-2">
                <Label>Nome do Contato *</Label>
                <Input
                  value={newDeal.person_name}
                  onChange={(e) => {
                    setNewDeal({...newDeal, person_name: e.target.value});
                    setDealFormErrors(prev => ({ ...prev, person_name: undefined }));
                  }}
                  placeholder="Nome completo do contato"
                  className={dealFormErrors.person_name ? 'border-red-400 focus-visible:ring-red-300' : ''}
                />
                {dealFormErrors.person_name && <p className="text-xs text-red-600"><Ico e="⚠" bare />{dealFormErrors.person_name}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newDeal.person_email}
                    onChange={(e) => {
                      setNewDeal({...newDeal, person_email: e.target.value});
                      setDealFormErrors(prev => ({ ...prev, person_email: undefined }));
                    }}
                    placeholder="email@exemplo.com"
                    className={dealFormErrors.person_email ? 'border-red-400 focus-visible:ring-red-300' : ''}
                  />
                  {dealFormErrors.person_email && <p className="text-xs text-red-600"><Ico e="⚠" bare />{dealFormErrors.person_email}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                <Input
                    value={newDeal.person_phone}
                    onChange={(e) => {
                      const masked = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 11)
                        .replace(/^(\d{2})(\d)/, '($1) $2')
                        .replace(/(\d{5})(\d)/, '$1-$2');
                      setNewDeal({...newDeal, person_phone: masked});
                    }}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Empresa/Organização</Label>
                  <Input
                    value={newDeal.organization_name}
                    onChange={(e) => setNewDeal({...newDeal, organization_name: e.target.value})}
                    placeholder="Nome da empresa"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Origem</Label>
                  <Select
                    value={newDeal.source}
                    onValueChange={(value) => setNewDeal({...newDeal, source: value})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual"><Ico e="🖊" />Manual</SelectItem>
                      <SelectItem value="website"><Ico e="🌐" />Website</SelectItem>
                      <SelectItem value="referral"><Ico e="👥" />Indicação</SelectItem>
                      <SelectItem value="social"><Ico e="📱" />Redes Sociais</SelectItem>
                      <SelectItem value="ads"><Ico e="📺" />Anúncios</SelectItem>
                      <SelectItem value="cold_call"><Ico e="📞" />Cold Call</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Esperada de Fechamento</Label>
                  <Input
                    type="date"
                    value={newDeal.expected_close_date}
                    onChange={(e) => setNewDeal({...newDeal, expected_close_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Próxima Atividade</Label>
                  <Select
                    value={newDeal.next_activity_type}
                    onValueChange={(value) => setNewDeal({...newDeal, next_activity_type: value})}
                  >
                    <SelectTrigger><SelectValue placeholder="Tipo de atividade" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call"><Ico e="📞" />Ligação</SelectItem>
                      <SelectItem value="email"><Ico e="📧" />Email</SelectItem>
                      <SelectItem value="meeting"><Ico e="🤝" />Reunião</SelectItem>
                      <SelectItem value="task"><Ico e="✅" />Tarefa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddDeal} className="btn-primary">
              Criar Negócio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deal Details Modal - Estilo Pipedrive */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <DialogTitle className="text-xl">{selectedDeal?.title}</DialogTitle>
                <DialogDescription>
                  {formatCurrency(selectedDeal?.value || 0)} • {selectedDeal?.stage}
                </DialogDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`${getProbabilityColor(selectedDeal?.probability || 0)}`}>
                  {selectedDeal?.probability || 0}%
                </Badge>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleMarkAsWon}
                >
                  <Ico e="✅" />Ganho
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => handleMarkAsLost()}
                >
                  <Ico e="❌" />Perdido
                </Button>
              </div>
            </div>
          </DialogHeader>

          {/* Tabs Navigation */}
          <div className="border-b border-slate-200 overflow-x-auto nx-scrollbar">
            <div className="flex space-x-4 sm:space-x-6 w-max min-w-full">
              {[
                { id: 'resumo', label: <><Ico e="📋" />Resumo</> },
                { id: 'cotacoes', label: <><Ico e="💼" />Cotações</> },
                { id: 'proposta', label: <><Ico e="📄" />Proposta</> },
                { id: 'projeto', label: <><Ico e="🚀" />Projeto</> },
                { id: 'atividades', label: <><Ico e="📅" />Atividades</> },
                { id: 'historico', label: <><Ico e="📜" />Histórico</> },
                { id: 'inteligencia', label: <><Ico e="🧠" />Inteligência</> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedDetailTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    selectedDetailTab === tab.id || (tab.id === 'atividades' && selectedDetailTab === 'nova-atividade')
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 max-h-[60vh]">
            {/* Resumo Tab */}
            {selectedDetailTab === 'resumo' && selectedDeal && (
              <div className="space-y-6">
                {/* Deal Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Informações do Negócio</Label>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600"><Ico e="💰" />Valor:</span>
                          <span className="font-semibold">{formatCurrency(selectedDeal.value)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600"><Ico e="📊" />Probabilidade:</span>
                          <span className="font-semibold">{selectedDeal.probability}%</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600"><Ico e="📍" />Estágio:</span>
                          <span className="font-semibold">{selectedDeal.stage}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600"><Ico e="🎯" />Status:</span>
                          <Badge className={
                            selectedDeal.status === 'won' ? 'bg-green-100 text-green-800' :
                            selectedDeal.status === 'lost' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }>
                            {selectedDeal.status === 'won' ? 'Ganho' : 
                             selectedDeal.status === 'lost' ? 'Perdido' : 'Aberto'}
                          </Badge>
                        </div>
                        {selectedDeal.expected_close_date && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-slate-600"><Ico e="📅" />Fechamento esperado:</span>
                            <span>{new Date(selectedDeal.expected_close_date).toLocaleDateString('pt-BR')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Contato</Label>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600"><Ico e="👤" />Nome:</span>
                          <span className="font-medium">{selectedDeal.person_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600"><Ico e="📧" />Email:</span>
                          <span>{selectedDeal.person_email}</span>
                        </div>
                        {selectedDeal.person_phone && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-slate-600"><Ico e="📱" />Telefone:</span>
                            <span>{selectedDeal.person_phone}</span>
                          </div>
                        )}
                        {selectedDeal.organization_name && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-slate-600"><Ico e="🏢" />Empresa:</span>
                            <span className="font-medium">{selectedDeal.organization_name}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600"><Ico e="🔗" />Origem:</span>
                          <span className="capitalize">{selectedDeal.source}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Next Activity */}
                {selectedDeal.next_activity_date && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-lg">{getActivityIcon(selectedDeal.next_activity_type)}</span>
                      <span className="font-medium text-yellow-800">Próxima Atividade</span>
                    </div>
                    <div className="text-sm text-yellow-700">
                      <p>Tipo: {selectedDeal.next_activity_type}</p>
                      <p>Data: {new Date(selectedDeal.next_activity_date).toLocaleString('pt-BR')}</p>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">Ações Rápidas</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline"><Ico e="📞" />Ligar</Button>
                    <Button size="sm" variant="outline"><Ico e="📧" />Email</Button>
                    <Button size="sm" variant="outline"><Ico e="🤝" />Agendar Reunião</Button>
                    <Button size="sm" variant="outline"><Ico e="📝" />Adicionar Nota</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Produtos Tab */}
            {selectedDetailTab === 'produtos' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Produtos do Negócio</h3>
                  <Button onClick={() => setShowProductModal(true)} size="sm">
                    <Ico e="➕" />Adicionar Produto
                  </Button>
                </div>

                {dealProducts.length > 0 ? (
                  <div className="space-y-3">
                    {dealProducts.map((product, index) => (
                      <Card key={index} className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{product.name}</h4>
                            <p className="text-sm text-slate-600">
                              {formatCurrency(product.unit_price)} x {product.quantity}
                              {product.discount_percent > 0 && ` (-${product.discount_percent}%)`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(product.total)}</p>
                            {product.tax_percent > 0 && (
                              <p className="text-xs text-slate-500">+{product.tax_percent}% imposto</p>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                    
                    <div className="border-t pt-4">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total:</span>
                        <span>{formatCurrency(dealProducts.reduce((sum, p) => sum + p.total, 0))}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <div className="text-4xl mb-2"><Ico e="📦" bare /></div>
                    <p>Nenhum produto adicionado</p>
                    <Button 
                      onClick={() => setShowProductModal(true)} 
                      className="mt-4"
                      variant="outline"
                    >
                      Adicionar Primeiro Produto
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Atividades Tab */}
            {(selectedDetailTab === 'atividades' || selectedDetailTab === 'nova-atividade') && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Atividades</h3>
                  <Button onClick={() => setSelectedDetailTab('nova-atividade')} size="sm">
                    <Ico e="➕" />Nova Atividade
                  </Button>
                </div>

                {dealActivities.length > 0 ? (
                  <div className="space-y-3">
                    {dealActivities.map((activity) => (
                      <Card key={activity.id} className="p-4">
                        <div className="flex items-start space-x-3">
                          <div className="text-lg">
                            {getActivityIcon(activity.activity_type)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium">{activity.subject}</h4>
                            {activity.description && (
                              <p className="text-sm text-slate-600 mt-1">{activity.description}</p>
                            )}
                            <div className="flex items-center space-x-4 mt-2 text-xs text-slate-500">
                              <span>{new Date(activity.created_at).toLocaleString('pt-BR')}</span>
                              {activity.due_date && (
                                <span>Vencimento: {new Date(activity.due_date).toLocaleString('pt-BR')}</span>
                              )}
                              {activity.duration_minutes && (
                                <span>Duração: {activity.duration_minutes}min</span>
                              )}
                            </div>
                          </div>
                          <Badge className={activity.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {activity.completed ? 'Concluída' : 'Pendente'}
                          </Badge>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <div className="text-4xl mb-2"><Ico e="📋" bare /></div>
                    <p>Nenhuma atividade registrada</p>
                  </div>
                )}

                {/* Nova Atividade Form */}
                {selectedDetailTab === 'nova-atividade' && (
                  <Card className="p-4 bg-blue-50">
                    <h4 className="font-medium mb-4">Nova Atividade</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Tipo</Label>
                          <Select 
                            value={newActivity.activity_type}
                            onValueChange={(value) => setNewActivity({...newActivity, activity_type: value})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="call"><Ico e="📞" />Ligação</SelectItem>
                              <SelectItem value="email"><Ico e="📧" />Email</SelectItem>
                              <SelectItem value="meeting"><Ico e="🤝" />Reunião</SelectItem>
                              <SelectItem value="task"><Ico e="✅" />Tarefa</SelectItem>
                              <SelectItem value="note"><Ico e="📝" />Anotação</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Duração (min)</Label>
                          <Input
                            type="number"
                            value={newActivity.duration_minutes}
                            onChange={(e) => setNewActivity({...newActivity, duration_minutes: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Assunto</Label>
                        <Input
                          value={newActivity.subject}
                          onChange={(e) => setNewActivity({...newActivity, subject: e.target.value})}
                          placeholder="Ex: Ligação de follow-up"
                        />
                      </div>
                      <div>
                        <Label>Data/Hora</Label>
                        <Input
                          type="datetime-local"
                          value={newActivity.due_date}
                          onChange={(e) => setNewActivity({...newActivity, due_date: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Descrição</Label>
                        <Textarea
                          value={newActivity.description}
                          onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
                          placeholder="Detalhes da atividade..."
                          rows={3}
                        />
                      </div>
                      <div className="flex space-x-2">
                        <Button onClick={handleAddActivity} size="sm">
                          Salvar Atividade
                        </Button>
                        <Button 
                          onClick={() => setSelectedDetailTab('atividades')} 
                          variant="outline" 
                          size="sm"
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            )}

            {selectedDetailTab === 'cotacoes' && selectedDeal && (
              <DealQuotesTab deal={selectedDeal} api={api} />
            )}

            {selectedDetailTab === 'proposta' && selectedDeal && (
              <DealProposalsTab deal={selectedDeal} api={api} />
            )}

            {selectedDetailTab === 'projeto' && selectedDeal && (
              <DealProjetoTab
                deal={selectedDeal}
                api={api}
                onNavigateToProject={() => { setShowDetailModal(false); setCurrentPage && setCurrentPage('projects'); }}
              />
            )}

            {selectedDetailTab === 'inteligencia' && selectedDeal && (
              <DealIntelligenceTab deal={selectedDeal} api={api} />
            )}

            {selectedDetailTab === 'historico' && selectedDeal && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Histórico do Negócio</h3>
                
                <div className="space-y-3">
                  {/* Timeline de mudanças */}
                  <Card className="p-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-xs"><Ico e="📋" bare /></span>
                      </div>
                      <div>
                        <p className="font-medium">Negócio criado</p>
                        <p className="text-sm text-slate-600">
                          Valor inicial: {formatCurrency(selectedDeal.value)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(selectedDeal.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  </Card>

                  {selectedDeal.stage_entered_at && (
                    <Card className="p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-xs"><Ico e="➡" bare /></span>
                        </div>
                        <div>
                          <p className="font-medium">Estágio atual: {selectedDeal.stage}</p>
                          <p className="text-sm text-slate-600">
                            Probabilidade: {selectedDeal.probability}%
                          </p>
                          <p className="text-xs text-slate-500">
                            Desde {new Date(selectedDeal.stage_entered_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    </Card>
                  )}

                  {selectedDeal.last_activity_date && (
                    <Card className="p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                          <span className="text-xs"><Ico e="⚡" bare /></span>
                        </div>
                        <div>
                          <p className="font-medium">Última atividade</p>
                          <p className="text-xs text-slate-500">
                            {new Date(selectedDeal.last_activity_date).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 flex items-center justify-between">
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => handleDeleteDeal(selectedDeal)}
            >
              <Ico e="🗑" />Excluir Negócio
            </Button>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Produto</DialogTitle>
            <DialogDescription>Configure o produto para este negócio</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nome do Produto *</Label>
              <Input
                value={newProduct.name}
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                placeholder="Ex: Sistema CRM Premium"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preço Unitário (R$)</Label>
                <Input
                  type="number"
                  value={newProduct.unit_price}
                  onChange={(e) => setNewProduct({...newProduct, unit_price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Quantidade</Label>
                <Input
                  type="number"
                  value={newProduct.quantity}
                  onChange={(e) => setNewProduct({...newProduct, quantity: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Desconto (%)</Label>
                <Input
                  type="number"
                  value={newProduct.discount_percent}
                  onChange={(e) => setNewProduct({...newProduct, discount_percent: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Imposto (%)</Label>
                <Input
                  type="number"
                  value={newProduct.tax_percent}
                  onChange={(e) => setNewProduct({...newProduct, tax_percent: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            
            {/* Cálculo do Total */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(newProduct.unit_price * newProduct.quantity)}</span>
                </div>
                {newProduct.discount_percent > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Desconto ({newProduct.discount_percent}%):</span>
                    <span>-{formatCurrency((newProduct.unit_price * newProduct.quantity * newProduct.discount_percent / 100))}</span>
                  </div>
                )}
                {newProduct.tax_percent > 0 && (
                  <div className="flex justify-between text-blue-600">
                    <span>Imposto ({newProduct.tax_percent}%):</span>
                    <span>+{formatCurrency((newProduct.unit_price * newProduct.quantity * (1 - newProduct.discount_percent / 100) * newProduct.tax_percent / 100))}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-1">
                  <span>Total:</span>
                  <span>{formatCurrency(newProduct.unit_price * newProduct.quantity * (1 - newProduct.discount_percent / 100) * (1 + newProduct.tax_percent / 100))}</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddProduct} disabled={!newProduct.name}>
              Adicionar Produto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Deal Modal */}
      <Dialog open={showEditDealModal} onOpenChange={setShowEditDealModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Negócio</DialogTitle>
            <DialogDescription>Modifique os dados do negócio selecionado</DialogDescription>
          </DialogHeader>
          
          {editingDeal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Título do Negócio *</Label>
                  <Input
                    value={editingDeal.title}
                    onChange={(e) => setEditingDeal({...editingDeal, title: e.target.value})}
                    placeholder="Ex: Venda sistema ERP"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Valor (R$)</Label>
                  <Input
                    value={editingDeal.value}
                    onChange={(e) => {
                      // Apply currency mask to input
                      const maskedValue = e.target.value.replace(/[^\d]/g, '');
                      const numberValue = parseFloat(maskedValue) / 100;
                      setEditingDeal({...editingDeal, value: numberValue || 0});
                    }}
                    placeholder="R$ 0,00"
                  />
                  <p className="text-xs text-slate-500">Digite apenas números</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estágio</Label>
                  <Select value={editingDeal.stage} onValueChange={(value) => setEditingDeal({...editingDeal, stage: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o estágio" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedPipeline?.stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.name}>
                          {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Probabilidade (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={editingDeal.probability}
                    onChange={(e) => setEditingDeal({...editingDeal, probability: e.target.value})}
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Nome do Contato *</Label>
                <Input
                  value={editingDeal.person_name}
                  onChange={(e) => setEditingDeal({...editingDeal, person_name: e.target.value})}
                  placeholder="Nome completo do contato"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email do Contato</Label>
                  <Input
                    type="email"
                    value={editingDeal.person_email}
                    onChange={(e) => setEditingDeal({...editingDeal, person_email: e.target.value})}
                    placeholder="email@exemplo.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={editingDeal.person_phone}
                    onChange={(e) => setEditingDeal({...editingDeal, person_phone: e.target.value})}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Empresa</Label>
                  <Input
                    value={editingDeal.organization_name}
                    onChange={(e) => setEditingDeal({...editingDeal, organization_name: e.target.value})}
                    placeholder="Nome da empresa"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Data Prevista de Fechamento</Label>
                  <Input
                    type="date"
                    value={editingDeal.expected_close_date}
                    onChange={(e) => setEditingDeal({...editingDeal, expected_close_date: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDealModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEditDeal} className="btn-primary">
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {draggedDeal && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
          <div className="bg-blue-100 border-2 border-blue-300 border-dashed rounded-lg p-4 shadow-lg">
            <p className="text-blue-700 font-medium">Movendo: {draggedDeal.title}</p>
            <p className="text-blue-600 text-sm">{formatCurrency(draggedDeal.value)}</p>
          </div>
        </div>
      )}
      {/* Drag Overlay */}
      {draggedDeal && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
          <div className="bg-blue-100 border-2 border-blue-300 border-dashed rounded-lg p-4 shadow-lg">
            <p className="text-blue-700 font-medium">Movendo: {draggedDeal.title}</p>
            <p className="text-blue-600 text-sm">{formatCurrency(draggedDeal.value)}</p>
          </div>
        </div>
      )}

      {/* Dialog: Marcar como Perdido + Concorrência */}
      <Dialog open={showLostDialog} onOpenChange={setShowLostDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle><Ico e="❌" />Marcar como Perdido</DialogTitle>
            <DialogDescription>Registre o motivo e, se possível, o concorrente que ganhou este negócio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da Perda</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                value={lostReasonInput}
                onChange={e => setLostReasonInput(e.target.value)}
              >
                <option value="">Selecione um motivo...</option>
                <option value="Preço alto">Preço alto</option>
                <option value="Perdido para concorrente">Perdido para concorrente</option>
                <option value="Sem orçamento">Sem orçamento</option>
                <option value="Projeto cancelado">Projeto cancelado</option>
                <option value="Sem resposta">Sem resposta / Ghosting</option>
                <option value="Timing inadequado">Timing inadequado</option>
                <option value="Decisão do cliente">Decisão do cliente</option>
                <option value="Produto não adequado">Produto não adequado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Perdido para qual concorrente? <span className="text-gray-400 font-normal">(opcional)</span></label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="Ex: Salesforce, HubSpot, Pipedrive..."
                value={lostCompetitor}
                onChange={e => setLostCompetitor(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLostDialog(false)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleConfirmLost}>
              Confirmar Perda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SentProposalsModal
        open={showSentProposals}
        onClose={() => setShowSentProposals(false)}
        api={api}
      />
    </div>
  );
}

export { ProposalSendModal, ProposalEmailHistoryModal };